/**
 * Stat Source Decomposition Utility
 * Mirrors EquipmentManager.recalculateStats() but returns structured
 * breakdown data showing where each bonus comes from.
 */

import { EquippedGear, EquipmentSlot } from '@/types';
import { EntityStats, calculateStatMultipliers } from '@/types/progression';
import { BALANCE_CONFIG } from '@/data/balance/balanceConfig';
import { getEquipmentDefinition } from '@/data/equipment/equipmentData';
import { SLOT_CONFIG } from '@/data/equipment/slotConfig';

export interface StatSource {
  label: string;
  icon?: string;
  value: number;
  formattedValue: string;
}

export interface StatBreakdown {
  finalValue: number;
  formattedFinal: string;
  sources: StatSource[];
  multipliers: StatSource[];
}

export interface AllStatBreakdowns {
  catchChance: StatBreakdown;
  fishingTime: StatBreakdown;
  qualityBonus: StatBreakdown;
  valueBonus: StatBreakdown;
}

interface EquipmentContribution {
  name: string;
  icon: string;
  fishingSpeed: number;
  catchChance: number;
  qualityBonus: number;
  valueBonus: number;
}

function gatherEquipmentContributions(equippedGear: EquippedGear): EquipmentContribution[] {
  const contributions: EquipmentContribution[] = [];

  for (const [slot, equipmentId] of Object.entries(equippedGear)) {
    if (!equipmentId) continue;
    try {
      const def = getEquipmentDefinition(equipmentId);
      const slotMeta = SLOT_CONFIG[slot as EquipmentSlot];
      contributions.push({
        name: def.name,
        icon: slotMeta?.icon || def.icon || '',
        fishingSpeed: def.stats.fishingSpeed || 0,
        catchChance: def.stats.catchChance || 0,
        qualityBonus: def.stats.qualityBonus || 0,
        valueBonus: def.stats.valueBonus || 0,
      });
    } catch {
      // Skip invalid equipment IDs
    }
  }

  return contributions;
}

function fmt(value: number): string {
  return `+${Math.round(value * 100)}%`;
}

function fmtMult(value: number): string {
  return `x${value.toFixed(3)}`;
}

export function computeAllStatBreakdowns(
  equippedGear: EquippedGear,
  entityStats: EntityStats,
): AllStatBreakdowns {
  const equipment = gatherEquipmentContributions(equippedGear);
  const multipliers = calculateStatMultipliers(entityStats);
  const luckBonus = entityStats.luck * 0.002;

  // === CATCH CHANCE ===
  const ccBase = BALANCE_CONFIG.BASE_CATCH_CHANCE;
  const ccEquipTotal = equipment.reduce((sum, eq) => sum + eq.catchChance, 0);
  const ccRaw = (ccBase + ccEquipTotal) * multipliers.catchChance;
  const ccFinal = Math.min(BALANCE_CONFIG.MAX_CATCH_CHANCE, ccRaw);

  const ccSources: StatSource[] = [
    { label: 'Base', value: ccBase, formattedValue: `${Math.round(ccBase * 100)}%` },
    ...equipment
      .filter((eq) => eq.catchChance > 0)
      .map((eq) => ({ label: eq.name, icon: eq.icon, value: eq.catchChance, formattedValue: fmt(eq.catchChance) })),
  ];
  const ccMultipliers: StatSource[] = [];
  if (entityStats.reflexes > 0) {
    ccMultipliers.push({ label: `Reflexes (Lv ${entityStats.reflexes})`, icon: '⚡', value: entityStats.reflexes * 0.015, formattedValue: fmtMult(1 + entityStats.reflexes * 0.015) });
  }
  if (entityStats.eyesight > 0) {
    ccMultipliers.push({ label: `Eyesight (Lv ${entityStats.eyesight})`, icon: '👁️', value: entityStats.eyesight * 0.005, formattedValue: fmtMult(1 + entityStats.eyesight * 0.005) });
  }
  if (entityStats.luck > 0) {
    ccMultipliers.push({ label: `Luck (Lv ${entityStats.luck})`, icon: '🍀', value: luckBonus, formattedValue: fmtMult(1 + luckBonus) });
  }

  // === FISHING TIME ===
  const ftBase = BALANCE_CONFIG.BASE_FISHING_TIME;
  const ftEquipSpeedTotal = equipment.reduce((sum, eq) => sum + eq.fishingSpeed, 0);
  const ftRaw = ftBase * (1 - ftEquipSpeedTotal) / multipliers.catchSpeed;
  const ftFinal = Math.max(BALANCE_CONFIG.MIN_FISHING_TIME, ftRaw);

  const ftSources: StatSource[] = [
    { label: 'Base', value: ftBase, formattedValue: `${ftBase.toFixed(1)}s` },
    ...equipment
      .filter((eq) => eq.fishingSpeed > 0)
      .map((eq) => ({ label: eq.name, icon: eq.icon, value: eq.fishingSpeed, formattedValue: `-${Math.round(eq.fishingSpeed * 100)}%` })),
  ];
  const ftMultipliers: StatSource[] = [];
  if (entityStats.eyesight > 0) {
    ftMultipliers.push({ label: `Eyesight (Lv ${entityStats.eyesight})`, icon: '👁️', value: entityStats.eyesight * 0.01, formattedValue: fmtMult(1 + entityStats.eyesight * 0.01) });
  }
  if (entityStats.physique > 0) {
    ftMultipliers.push({ label: `Physique (Lv ${entityStats.physique})`, icon: '💪', value: entityStats.physique * 0.005, formattedValue: fmtMult(1 + entityStats.physique * 0.005) });
  }
  if (entityStats.luck > 0) {
    ftMultipliers.push({ label: `Luck (Lv ${entityStats.luck})`, icon: '🍀', value: luckBonus, formattedValue: fmtMult(1 + luckBonus) });
  }

  // === QUALITY BONUS ===
  const qbEquipTotal = equipment.reduce((sum, eq) => sum + eq.qualityBonus, 0);
  const qbFinal = qbEquipTotal + multipliers.qualityBonus;

  const qbSources: StatSource[] = [
    ...equipment
      .filter((eq) => eq.qualityBonus > 0)
      .map((eq) => ({ label: eq.name, icon: eq.icon, value: eq.qualityBonus, formattedValue: fmt(eq.qualityBonus) })),
  ];
  const qbMultipliers: StatSource[] = [];
  if (entityStats.finesse > 0) {
    qbMultipliers.push({ label: `Finesse (Lv ${entityStats.finesse})`, icon: '✨', value: entityStats.finesse * 0.015, formattedValue: fmt(entityStats.finesse * 0.015) });
  }
  if (entityStats.luck > 0) {
    qbMultipliers.push({ label: `Luck (Lv ${entityStats.luck})`, icon: '🍀', value: luckBonus, formattedValue: fmt(luckBonus) });
  }

  // === VALUE BONUS ===
  const vbEquipTotal = equipment.reduce((sum, eq) => sum + eq.valueBonus, 0);
  const vbFinal = vbEquipTotal + multipliers.valueBonus;

  const vbSources: StatSource[] = [
    ...equipment
      .filter((eq) => eq.valueBonus > 0)
      .map((eq) => ({ label: eq.name, icon: eq.icon, value: eq.valueBonus, formattedValue: fmt(eq.valueBonus) })),
  ];
  const vbMultipliers: StatSource[] = [];
  if (entityStats.physique > 0) {
    vbMultipliers.push({ label: `Physique (Lv ${entityStats.physique})`, icon: '💪', value: entityStats.physique * 0.01, formattedValue: fmt(entityStats.physique * 0.01) });
  }
  if (entityStats.luck > 0) {
    vbMultipliers.push({ label: `Luck (Lv ${entityStats.luck})`, icon: '🍀', value: luckBonus, formattedValue: fmt(luckBonus) });
  }

  return {
    catchChance: {
      finalValue: ccFinal,
      formattedFinal: `${Math.round(ccFinal * 100)}%`,
      sources: ccSources,
      multipliers: ccMultipliers,
    },
    fishingTime: {
      finalValue: ftFinal,
      formattedFinal: `${ftFinal.toFixed(1)}s`,
      sources: ftSources,
      multipliers: ftMultipliers,
    },
    qualityBonus: {
      finalValue: qbFinal,
      formattedFinal: `+${Math.round(qbFinal * 100)}%`,
      sources: qbSources,
      multipliers: qbMultipliers,
    },
    valueBonus: {
      finalValue: vbFinal,
      formattedFinal: `+${Math.round(vbFinal * 100)}%`,
      sources: vbSources,
      multipliers: vbMultipliers,
    },
  };
}
